/**
 * Hand Gesture Controller — Minority Report UI
 * Uses MediaPipe HandLandmarker in browser for gesture recognition.
 * Pinch = Click, Fist = Drag, Open Palm = Release
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FilesetResolver, HandLandmarker } from '@mediapipe/tasks-vision';
import { Hand, Video, VideoOff } from 'lucide-react';

export default function GestureController({ enabled, onToggle }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const handLandmarkerRef = useRef(null);
  const animFrameRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [isPinching, setIsPinching] = useState(false);
  const [handDetected, setHandDetected] = useState(false);
  const smoothedPos = useRef({ x: 0, y: 0 });
  const lastClickTime = useRef(0);

  // Initialize MediaPipe HandLandmarker
  useEffect(() => {
    const init = async () => {
      try {
        console.log('[HAND] Initializing MediaPipe...');
        const vision = await FilesetResolver.forVisionTasks(
          '/mediapipe'
        );
        
        handLandmarkerRef.current = await HandLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: '/mediapipe/hand_landmarker.task',
            delegate: 'GPU'
          },
          runningMode: 'VIDEO',
          numHands: 1
        });
        
        setIsReady(true);
        console.log('[HAND] MediaPipe HandLandmarker ready!');
      } catch (e) {
        console.error('[HAND] Init error:', e);
      }
    };
    
    init();
    
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // Start/stop camera
  const toggleCamera = useCallback(async () => {
    if (cameraOn) {
      // Stop
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(t => t.stop());
        videoRef.current.srcObject = null;
      }
      setCameraOn(false);
      setHandDetected(false);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setCameraOn(true);
        
        // Wait for video to be ready
        videoRef.current.onloadeddata = () => {
          detectHands();
        };
      }
    } catch (e) {
      console.error('[HAND] Camera error:', e);
    }
  }, [cameraOn]);

  // Hand detection loop
  const detectHands = useCallback(() => {
    const video = videoRef.current;
    if (!video || !handLandmarkerRef.current || video.readyState < 2) {
      animFrameRef.current = requestAnimationFrame(detectHands);
      return;
    }

    const results = handLandmarkerRef.current.detectForVideo(video, performance.now());
    
    if (results.landmarks && results.landmarks.length > 0) {
      const landmarks = results.landmarks[0];
      setHandDetected(true);
      
      // Index finger tip (8) and thumb tip (4)
      const indexTip = landmarks[8];
      const thumbTip = landmarks[4];
      
      // Map to screen coordinates (mirror X for natural interaction)
      const SENSITIVITY = 2.0;
      const rawX = 1 - indexTip.x; // Mirror
      const normX = Math.max(0, Math.min(1, (rawX - 0.5) * SENSITIVITY + 0.5));
      const normY = Math.max(0, Math.min(1, (indexTip.y - 0.5) * SENSITIVITY + 0.5));
      
      const targetX = normX * window.innerWidth;
      const targetY = normY * window.innerHeight;
      
      // Smooth cursor (lerp)
      const LERP = 0.25;
      smoothedPos.current.x += (targetX - smoothedPos.current.x) * LERP;
      smoothedPos.current.y += (targetY - smoothedPos.current.y) * LERP;
      
      setCursorPos({ x: smoothedPos.current.x, y: smoothedPos.current.y });
      
      // Pinch detection
      const dist = Math.sqrt(
        Math.pow(indexTip.x - thumbTip.x, 2) + 
        Math.pow(indexTip.y - thumbTip.y, 2)
      );
      
      const pinching = dist < 0.05;
      
      if (pinching && !isPinching) {
        // Pinch started = Click
        const now = Date.now();
        if (now - lastClickTime.current > 300) { // Debounce
          lastClickTime.current = now;
          const el = document.elementFromPoint(smoothedPos.current.x, smoothedPos.current.y);
          if (el) {
            const clickable = el.closest('button, input, a, [role="button"], [data-testid]');
            if (clickable) {
              clickable.click();
              console.log('[HAND] Clicked:', clickable.tagName, clickable.getAttribute('data-testid'));
            }
          }
        }
      }
      
      setIsPinching(pinching);
      
      // Draw skeleton on canvas
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        canvasRef.current.width = video.videoWidth;
        canvasRef.current.height = video.videoHeight;
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        
        // Draw hand connections
        ctx.strokeStyle = '#00FFFF';
        ctx.lineWidth = 2;
        const connections = HandLandmarker.HAND_CONNECTIONS;
        for (const conn of connections) {
          const start = landmarks[conn.start];
          const end = landmarks[conn.end];
          ctx.beginPath();
          ctx.moveTo(start.x * canvasRef.current.width, start.y * canvasRef.current.height);
          ctx.lineTo(end.x * canvasRef.current.width, end.y * canvasRef.current.height);
          ctx.stroke();
        }
        
        // Draw landmarks
        landmarks.forEach((lm, i) => {
          ctx.beginPath();
          ctx.arc(lm.x * canvasRef.current.width, lm.y * canvasRef.current.height, 3, 0, Math.PI * 2);
          ctx.fillStyle = i === 8 || i === 4 ? '#FF0000' : '#00FFFF';
          ctx.fill();
        });
      }
    } else {
      setHandDetected(false);
    }
    
    animFrameRef.current = requestAnimationFrame(detectHands);
  }, [isPinching]);

  if (!enabled) return null;

  return (
    <>
      {/* Hand cursor overlay */}
      {cameraOn && handDetected && (
        <div
          style={{
            position: 'fixed',
            left: cursorPos.x,
            top: cursorPos.y,
            transform: 'translate(-50%, -50%)',
            width: isPinching ? '20px' : '28px',
            height: isPinching ? '20px' : '28px',
            borderRadius: '50%',
            border: `2px solid ${isPinching ? '#00FF87' : '#00D7FF'}`,
            background: isPinching ? 'rgba(0, 255, 135, 0.2)' : 'transparent',
            boxShadow: isPinching 
              ? '0 0 20px rgba(0,255,135,0.6)' 
              : '0 0 12px rgba(0,215,255,0.3)',
            pointerEvents: 'none',
            zIndex: 9999,
            transition: 'width 0.1s, height 0.1s, background 0.1s',
          }}
        >
          <div style={{
            position: 'absolute',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '3px', height: '3px',
            borderRadius: '50%',
            background: '#fff',
          }} />
        </div>
      )}

      {/* Camera feed (small, bottom-left) */}
      <div style={{
        position: 'fixed',
        bottom: '70px',
        left: '16px',
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        alignItems: 'flex-start',
      }}>
        {/* Toggle button */}
        <button
          onClick={toggleCamera}
          style={{
            padding: '8px 14px',
            borderRadius: '8px',
            border: `1px solid ${cameraOn ? 'rgba(0,215,255,0.5)' : 'rgba(0,215,255,0.2)'}`,
            background: cameraOn ? 'rgba(0,215,255,0.1)' : 'rgba(0,0,0,0.6)',
            color: cameraOn ? '#00D7FF' : '#7C8AA6',
            fontFamily: 'var(--font-mono)',
            fontSize: '10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            letterSpacing: '0.1em',
          }}
          data-testid="gesture-toggle"
        >
          <Hand size={14} />
          {cameraOn ? 'GESTURES ON' : 'GESTURES OFF'}
          {handDetected && <span style={{ color: '#00FF87' }}>TRACKING</span>}
        </button>

        {/* Video preview */}
        {cameraOn && (
          <div style={{
            position: 'relative',
            width: '200px',
            height: '150px',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid rgba(0,215,255,0.2)',
          }}>
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)', // Mirror
              }}
              playsInline
              muted
            />
            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%',
                height: '100%',
                transform: 'scaleX(-1)', // Mirror to match video
                pointerEvents: 'none',
              }}
            />
            {!handDetected && (
              <div style={{
                position: 'absolute',
                bottom: '4px', left: '4px',
                fontFamily: 'var(--font-mono)',
                fontSize: '9px',
                color: '#FFB020',
                background: 'rgba(0,0,0,0.7)',
                padding: '2px 6px',
                borderRadius: '4px',
              }}>
                NO HAND DETECTED
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
